/**
 * PayPal Integration Test Page
 * 
 * This page provides a comprehensive testing interface for the PayPal integration:
 * - Connection management per mandate
 * - Real-time sync testing
 * - Transaction overview
 * - Classification results
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, CheckCircle, XCircle, RefreshCw, ExternalLink, CreditCard, DollarSign, TrendingUp } from 'lucide-react';
import { useMandateContext } from '../contexts/mandate-context';
import { useAuth } from '../contexts/auth-context';
import { supabase } from '../integrations/supabase/client';
import PayPalIntegrationSection from '../components/settings/PayPalIntegrationSection';

interface PayPalConnection {
  id: string;
  mandate_id: string;
  paypal_account_email: string;
  connection_status: 'active' | 'disconnected' | 'error';
  last_synced_at: string | null;
  created_at: string;
}

interface PayPalSyncLog {
  id: string;
  sync_start_time: string;
  sync_end_time: string | null;
  status: 'success' | 'failed' | 'in_progress';
  transactions_fetched: number;
  transactions_processed: number;
  errors: any;
}

interface PayPalTransaction {
  id: string;
  transaction_date: string;
  amount: number;
  currency: string;
  description: string;
  counterparty: string;
  debit_account: string | null;
  credit_account: string | null;
  swiss_vat_rate: number;
  ai_classification_confidence: number;
  exchange_rate: number;
  chf_equivalent_amount: number;
  exchange_rate_source: string;
  ai_classification: any;
}

const PayPalTestPage: React.FC = () => {
  const { user, profile, organization } = useAuth();
  const { selectedMandateId } = useMandateContext();

  const [connections, setConnections] = useState<PayPalConnection[]>([]);
  const [syncLogs, setSyncLogs] = useState<PayPalSyncLog[]>([]);
  const [transactions, setTransactions] = useState<PayPalTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchPayPalData();
    }
  }, [user, selectedMandateId]);

  const fetchPayPalData = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Mock data for now - PayPal tables not yet implemented
      setConnections([]);
      setSyncLogs([]);
      setTransactions([]);
      
      console.log('PayPal data fetch - using mock data for now');
    } catch (err) {
      console.error('Error fetching PayPal data:', err);
      setError('Failed to load PayPal data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (connectionId: string, mandateId: string) => {
    if (!user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('paypal-sync', {
        body: {
          connectionId,
          mandateId,
          organizationId: profile?.organization_id
        }
      });

      if (error) throw error;

      console.log('PayPal sync completed:', data);
      await fetchPayPalData(); // Refresh data
    } catch (err) {
      console.error('Error syncing PayPal:', err);
      setError('Failed to sync PayPal transactions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'disconnected':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Disconnected</Badge>;
      case 'error':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Error</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSyncStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-100 text-green-800">Success</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'in_progress':
        return <Badge variant="secondary">In Progress</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-CH');
  };

  if (loading && connections.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2">Loading PayPal integration...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">PayPal Integration Test</h1>
          <p className="text-muted-foreground">Test and manage your PayPal integrations</p>
        </div>
        <Button onClick={fetchPayPalData} disabled={loading} variant="outline">
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="connections" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="sync">Sync Logs</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="integration">Integration UI</TabsTrigger>
        </TabsList>

        {/* Connections Tab */}
        <TabsContent value="connections" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>PayPal Connections</CardTitle>
              <CardDescription>Manage PayPal account connections per mandate</CardDescription>
            </CardHeader>
            <CardContent>
              {connections.length === 0 ? (
                <p className="text-muted-foreground">No PayPal connections found.</p>
              ) : (
                <div className="space-y-4">
                  {connections.map((connection) => (
                    <div key={connection.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{connection.paypal_account_email}</span>
                          {getStatusBadge(connection.connection_status)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Mandate: {connection.mandate_id}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Last sync: {connection.last_synced_at ? formatDate(connection.last_synced_at) : 'Never'}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleSync(connection.id, connection.mandate_id)}
                          disabled={loading || connection.connection_status !== 'active'}
                          size="sm"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Sync
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Logs Tab */}
        <TabsContent value="sync" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Sync Logs</CardTitle>
              <CardDescription>Recent PayPal synchronization attempts</CardDescription>
            </CardHeader>
            <CardContent>
              {syncLogs.length === 0 ? (
                <p className="text-muted-foreground">No sync logs found.</p>
              ) : (
                <div className="space-y-2">
                  {syncLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 border rounded">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {formatDate(log.sync_start_time)}
                          </span>
                          {getSyncStatusBadge(log.status)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Fetched: {log.transactions_fetched} | Processed: {log.transactions_processed}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {log.sync_end_time ? (
                          `${Math.round(
                            (new Date(log.sync_end_time).getTime() - new Date(log.sync_start_time).getTime()) / 1000
                          )}s`
                        ) : (
                          'In progress...'
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>PayPal Transactions</CardTitle>
              <CardDescription>Recently imported PayPal transactions</CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <p className="text-muted-foreground">No PayPal transactions found.</p>
              ) : (
                <div className="space-y-2">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-3 border rounded">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{tx.description}</span>
                          <Badge variant="outline">
                            {tx.ai_classification?.is_business ? 'Business' : 'Private'}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {tx.counterparty} • {formatDate(tx.transaction_date)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Account: {tx.debit_account || tx.credit_account} | 
                          Confidence: {tx.ai_classification_confidence}% |
                          VAT: {tx.swiss_vat_rate}%
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium">
                          {formatCurrency(tx.amount, tx.currency)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          CHF {tx.chf_equivalent_amount?.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integration UI Tab */}
        <TabsContent value="integration" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>PayPal Integration UI</CardTitle>
              <CardDescription>Test the PayPal integration interface</CardDescription>
            </CardHeader>
            <CardContent>
              <PayPalIntegrationSection />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayPalTestPage;
